import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { companyInfo } from '../data';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Format the message for WhatsApp
    const whatsappMessage = `
*New Inquiry from Al Abeer Diamond Website*

*Name:* ${formData.name}
*Email:* ${formData.email}
*Phone:* ${formData.phone || 'Not provided'}
*Subject:* ${formData.subject || 'Not provided'}

*Message:*
${formData.message}

---
*Sent from:* ${window.location.href}
    `.trim();

    // WhatsApp number (using the company's main phone number)
    const whatsappNumber = companyInfo.phone1.replace(/\s/g, '').replace('+', '');
    
    // Create WhatsApp URL
    const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(whatsappMessage)}`;

    // Open WhatsApp in a new window/tab
    window.open(whatsappUrl, '_blank');

    // Show success message
    setSubmitStatus('success');
    setIsSubmitting(false);
    
    // Reset form after a delay
    setTimeout(() => {
      setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
      setSubmitStatus(null);
    }, 5000);
  };

  const contactInfo = [
    { icon: 'uil uil-location-pin-alt', label: 'Address', value: companyInfo.address },
    { 
      icon: 'uil uil-phone-volume', 
      label: 'Phone', 
      value: companyInfo.phone1, 
      link: `tel:${companyInfo.phone1.replace(/\s/g, '')}` 
    },
    { 
      icon: 'uil uil-whatsapp', 
      label: 'WhatsApp', 
      value: 'Chat with us', 
      link: `https://wa.me/${companyInfo.phone1.replace(/\s/g, '').replace('+', '')}`,
      isWhatsApp: true
    },
    { 
      icon: 'uil uil-envelope', 
      label: 'Email', 
      value: companyInfo.email, 
      link: `mailto:${companyInfo.email}` 
    },
    { 
      icon: 'uil uil-globe', 
      label: 'Website', 
      value: companyInfo.website, 
      link: `https://${companyInfo.website}` 
    },
  ];

  return (
    <>
      <Helmet>
        <title>Contact Us | Al Abeer Diamond</title>
        <meta name="description" content={`Get in touch with ${companyInfo.name} for all your construction, engineering, and maintenance needs in Saudi Arabia.`} />
        <meta name="keywords" content="contact construction company saudi arabia, construction services, building contractors" />
      </Helmet>

      {/* Hero Section */}
      <section className="relative min-h-[40vh] flex items-center overflow-hidden bg-secondary">
        <div className="absolute inset-0 bg-hero-gradient opacity-90"></div>
        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl"
          >
            <span className="inline-block px-4 py-2 bg-primary/20 text-primary-light rounded-full text-sm font-medium font-oswald tracking-wider mb-4">
              Get In Touch
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white font-oswald leading-tight">
              Contact <br />
              <span className="text-primary-light">Us</span>
            </h1>
            <p className="text-gray-300 text-lg mt-4 max-w-2xl">
              Ready to start your project? Contact us today for a consultation.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Content */}
      <section className="section-padding bg-gray-50">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Contact Information */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="lg:col-span-1"
            >
              <div className="bg-white rounded-2xl p-6 shadow-soft">
                <h3 className="text-xl font-bold text-secondary font-oswald mb-6">
                  Contact Information
                </h3>
                <div className="space-y-4">
                  {contactInfo.map((item, index) => (
                    <div key={index} className="flex items-start space-x-4">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                        item.isWhatsApp ? 'bg-green-500' : 'bg-primary/10'
                      }`}>
                        <i className={`${item.icon} ${item.isWhatsApp ? 'text-white' : 'text-primary'}`}></i>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-secondary">{item.label}</p>
                        {item.link ? (
                          <a 
                            href={item.link} 
                            target={item.isWhatsApp ? "_blank" : "_self"}
                            rel={item.isWhatsApp ? "noopener noreferrer" : ""}
                            className={`text-sm transition-colors ${
                              item.isWhatsApp 
                                ? 'text-green-600 hover:text-green-700 font-medium' 
                                : 'text-gray-600 hover:text-primary'
                            }`}
                          >
                            {item.value}
                          </a>
                        ) : (
                          <p className="text-sm text-gray-600">{item.value}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Business Hours */}
                <div className="mt-6 pt-6 border-t border-gray-100">
                  <h4 className="font-oswald font-semibold text-secondary mb-3">Business Hours (KSA Time)</h4>
                  <div className="space-y-2 text-sm text-gray-600">
                    <div className="flex justify-between">
                      <span>Saturday - Thursday</span>
                      <span>8:00 AM - 6:00 PM</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Friday</span>
                      <span>Closed</span>
                    </div>
                  </div>
                </div>

                {/* Quick WhatsApp Button */}
                <div className="mt-6 pt-6 border-t border-gray-100">
                  <a
                    href={`https://wa.me/${companyInfo.phone1.replace(/\s/g, '').replace('+', '')}?text=${encodeURIComponent('Hello Al Abeer Diamond, I would like to inquire about your services.')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center space-x-2 bg-green-500 hover:bg-green-600 text-white px-4 py-3 rounded-lg transition-all duration-300 w-full"
                  >
                    <i className="uil uil-whatsapp text-xl"></i>
                    <span className="font-medium">Chat on WhatsApp</span>
                  </a>
                </div>
              </div>
            </motion.div>

            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="lg:col-span-2"
            >
              <div className="bg-white rounded-2xl p-6 shadow-soft">
                <h3 className="text-xl font-bold text-secondary font-oswald mb-6">
                  Send Us a Message
                </h3>
                <p className="text-gray-500 text-sm mb-6">
                  Fill in the form below and we'll redirect you to WhatsApp with your inquiry details.
                </p>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-secondary mb-1">
                        Full Name *
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition"
                        placeholder="Your full name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-secondary mb-1">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition"
                        placeholder="your@email.com"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-secondary mb-1">
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition"
                        placeholder="+966 5X XXX XXXX"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-secondary mb-1">
                        Subject
                      </label>
                      <input
                        type="text"
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition"
                        placeholder="Project inquiry"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-secondary mb-1">
                      Message *
                    </label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows="5"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition resize-none"
                      placeholder="Tell us about your project..."
                    ></textarea>
                  </div>
                  
                  <div className="flex items-center justify-between flex-wrap gap-4">
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="btn-primary flex items-center justify-center"
                    >
                      {isSubmitting ? (
                        <>
                          <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Processing...
                        </>
                      ) : (
                        <>
                          <i className="uil uil-whatsapp text-xl mr-2"></i>
                          Send via WhatsApp
                        </>
                      )}
                    </button>
                    
                    <p className="text-xs text-gray-400">
                      <i className="uil uil-lock-alt mr-1"></i>
                      Your data will be sent securely via WhatsApp
                    </p>
                  </div>
                  
                  {submitStatus === 'success' && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="p-4 bg-green-50 text-green-700 rounded-lg border border-green-200"
                    >
                      <i className="uil uil-check-circle mr-2"></i>
                      Opening WhatsApp... Please send the message to complete your inquiry.
                    </motion.div>
                  )}
                </form>
              </div>
            </motion.div>
          </div>

          {/* Map Section */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="mt-12"
          >
            <div className="bg-white rounded-2xl overflow-hidden shadow-soft">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3710.123456789012!2d39.172123!3d21.543212!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x15c3d1234567890b%3A0x1234567890abcdef!2sOld%20Makkah%20Road%2C%20Al%20Mursalat%2C%20Jeddah%20Saudi%20Arabia!5e0!3m2!1sen!2sus!4v1748419750000!5m2!1sen!2sus"
                width="100%"
                height="400"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                title="Al Abeer Diamond Location"
                className="w-full"
              ></iframe>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default Contact;